/**
 * PaymentPanel — Renders 3 payment methods on an approved quote:
 * Stripe (card), PayPal, Bonifico bancario.
 */
import { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/config";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CreditCard, Landmark, Loader2, Copy, Check, ExternalLink } from "lucide-react";

const CopyBtn = ({ text, testId }) => {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(text); setOk(true); setTimeout(() => setOk(false), 1500); }}
      data-testid={testId}
      className="ml-3 text-white/50 hover:text-[#ff4b00]"
      title="Copia"
    >
      {ok ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
};

const RowKV = ({ label, value, testId }) => (
  <div className="flex items-center justify-between py-2 border-b border-[#141414]">
    <span className="text-white/50 font-mono-tech text-[10px] uppercase">{label}</span>
    <div className="flex items-center">
      <span data-testid={testId} className="font-mono-tech text-sm text-white select-all">{value}</span>
      {value && <CopyBtn text={value} testId={`${testId}-copy`} />}
    </div>
  </div>
);

export default function PaymentPanel({ quote, onPaid }) {
  const [methods, setMethods] = useState(null);
  const [chosen, setChosen] = useState(null); // "stripe" | "paypal" | "bonifico"
  const [busy, setBusy] = useState(false);
  const [bonificoData, setBonificoData] = useState(null);

  useEffect(() => {
    api.get("/payments/methods").then((r) => setMethods(r.data)).catch(() => setMethods({}));
  }, []);

  const total = useMemo(
    () => (quote.final_price + quote.shipping_cost).toFixed(2),
    [quote],
  );

  const goStripe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", { quote_id: quote.id, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e, "Errore avvio pagamento"));
      setBusy(false);
    }
  };

  const requestBonifico = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/bonifico/request", { quote_id: quote.id });
      setBonificoData(r.data);
      setChosen("bonifico");
      toast.success("Coordinate bonifico generate");
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    } finally { setBusy(false); }
  };

  if (!methods) return <div className="text-white/40 font-mono-tech text-xs mt-10">Caricamento metodi di pagamento…</div>;

  const enabledCount = ["stripe", "paypal", "bonifico"].filter((k) => methods[k]?.enabled).length;
  if (enabledCount === 0) {
    return <div className="mt-10 border border-red-500/40 p-6 text-red-400 text-sm">Nessun metodo di pagamento configurato — contatta l&apos;amministratore.</div>;
  }

  return (
    <div className="mt-10 border border-[#ff4b00] bg-[#ff4b00]/5 p-6 md:p-8" data-testid="payment-panel">
      <div className="font-mono-tech text-[#ff4b00] mb-2">◈ Preventivo approvato</div>
      <div className="font-display text-2xl md:text-3xl mb-1">Scegli come pagare</div>
      <div className="font-mono-tech text-[11px] text-white/60 mb-8">Totale <span className="text-white">€ {total}</span> · alla conferma passiamo in produzione.</div>

      {/* Method chooser */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        {methods.stripe?.enabled && (
          <button
            onClick={() => setChosen("stripe")}
            data-testid="pay-choose-stripe"
            className={`p-4 border text-left transition-all ${chosen === "stripe" ? "border-[#ff4b00] bg-black" : "border-[#1a1a1a] bg-[#0a0a0a] hover:border-white/30"}`}
          >
            <CreditCard size={18} className="text-[#ff4b00] mb-2" />
            <div className="font-display text-lg">Carta di credito</div>
            <div className="font-mono-tech text-[10px] text-white/50 mt-1">Stripe · sicuro · immediato</div>
          </button>
        )}
        {methods.paypal?.enabled && (
          <button
            onClick={() => setChosen("paypal")}
            data-testid="pay-choose-paypal"
            className={`p-4 border text-left transition-all ${chosen === "paypal" ? "border-[#ff4b00] bg-black" : "border-[#1a1a1a] bg-[#0a0a0a] hover:border-white/30"}`}
          >
            <div className="mb-2"><span className="font-display text-lg text-[#0070ba]">Pay</span><span className="font-display text-lg text-[#003087]">Pal</span></div>
            <div className="font-display text-lg">PayPal</div>
            <div className="font-mono-tech text-[10px] text-white/50 mt-1">Conto PayPal · immediato</div>
          </button>
        )}
        {methods.bonifico?.enabled && (
          <button
            onClick={requestBonifico}
            disabled={busy}
            data-testid="pay-choose-bonifico"
            className={`p-4 border text-left transition-all ${chosen === "bonifico" ? "border-[#ff4b00] bg-black" : "border-[#1a1a1a] bg-[#0a0a0a] hover:border-white/30"}`}
          >
            <Landmark size={18} className="text-[#ff4b00] mb-2" />
            <div className="font-display text-lg">Bonifico bancario</div>
            <div className="font-mono-tech text-[10px] text-white/50 mt-1">1-2 giorni lavorativi</div>
          </button>
        )}
      </div>

      {/* Chosen method panel */}
      {chosen === "stripe" && (
        <div className="border border-[#1a1a1a] p-6 bg-[#0a0a0a] flex items-center justify-between gap-4">
          <div>
            <div className="font-display text-lg">Paga con carta</div>
            <div className="font-mono-tech text-[10px] text-white/50 mt-1">Verrai reindirizzato al checkout sicuro di Stripe.</div>
          </div>
          <button
            onClick={goStripe}
            disabled={busy}
            data-testid="quote-pay-btn"
            className="btn-primary"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <><CreditCard size={14} /> Paga € {total}</>}
          </button>
        </div>
      )}

      {chosen === "paypal" && methods.paypal?.client_id && (
        <div className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]" data-testid="paypal-buttons-wrapper">
          <div className="font-display text-lg mb-2">Paga con PayPal · € {total}</div>
          <div className="font-mono-tech text-[10px] text-white/50 mb-4">
            Ambiente: <span className="text-white">{methods.paypal.mode}</span>. Non è richiesta la carta se hai già un conto PayPal.
          </div>
          <PayPalScriptProvider
            options={{
              clientId: methods.paypal.client_id,
              currency: "EUR",
              intent: "capture",
              components: "buttons",
            }}
          >
            <PayPalButtons
              style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
              createOrder={async () => {
                try {
                  const r = await api.post("/payments/paypal/create-order", { quote_id: quote.id });
                  return r.data.order_id;
                } catch (e) {
                  toast.error(errMsg(e, "Errore creazione ordine PayPal"));
                  throw e;
                }
              }}
              onApprove={async (data) => {
                try {
                  const r = await api.post("/payments/paypal/capture-order", {
                    quote_id: quote.id,
                    order_id: data.orderID,
                  });
                  if (r.data.ok) {
                    toast.success("Pagamento completato!");
                    if (onPaid) onPaid();
                    else window.location.reload();
                  } else {
                    toast.error(`Stato PayPal: ${r.data.status}`);
                  }
                } catch (e) {
                  toast.error(errMsg(e, "Errore cattura pagamento"));
                }
              }}
              onError={(err) => {
                console.error("PayPal error:", err);
                toast.error("Errore PayPal — riprova o scegli un altro metodo");
              }}
              onCancel={() => toast.info("Pagamento PayPal annullato")}
            />
          </PayPalScriptProvider>
        </div>
      )}

      {chosen === "bonifico" && bonificoData && (
        <div className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]" data-testid="bonifico-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-display text-xl">Coordinate bonifico</div>
              <div className="font-mono-tech text-[10px] text-white/50 mt-1">Effettua il bonifico entro 7 giorni. Riceverai conferma via email quando lo riceviamo.</div>
            </div>
            <div className="text-right">
              <div className="font-mono-tech text-[10px] text-white/50">TOTALE</div>
              <div className="font-display text-2xl text-[#ff4b00]">€ {bonificoData.amount.toFixed(2)}</div>
            </div>
          </div>
          <div className="space-y-0">
            <RowKV label="Beneficiario" value={bonificoData.holder} testId="bonifico-holder" />
            <RowKV label="Banca" value={bonificoData.bank} testId="bonifico-bank" />
            <RowKV label="IBAN" value={bonificoData.iban} testId="bonifico-iban" />
            {bonificoData.bic && <RowKV label="BIC / SWIFT" value={bonificoData.bic} testId="bonifico-bic" />}
            <RowKV label="Causale" value={bonificoData.causale} testId="bonifico-causale" />
          </div>
          <div className="mt-6 p-4 border-l-2 border-[#ff4b00] bg-black/40 font-mono-tech text-[11px] text-white/70">
            <strong className="text-white">Importante:</strong> usa esattamente la causale sopra riportata, ci aiuta a riconciliare il pagamento più velocemente.
          </div>
        </div>
      )}
    </div>
  );
}
